import axios, { AxiosInstance } from 'axios';

// Handling the totus API

export const api: AxiosInstance = axios.create({
  baseURL: window.urls?.api,
  withCredentials: true,
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFToken',
  paramsSerializer(params) {
    /**
     * So we can pass arrays as params
     * Omits nulls
     */
    const searchParams = new URLSearchParams();
    for (const key of Object.keys(params)) {
      const param = params[key];
      if (Array.isArray(param)) {
        for (const p of param) {
          if (p !== null) {
            searchParams.append(key, p);
          }
        }
      } else {
        if (param !== null) {
          searchParams.append(key, param);
        }
      }
    }
    return searchParams.toString();
  },
});

/**
 * Stores state of a request to API
 * A very simple state for redux used by multiple features
 */
export interface APIRequestState {
  pending: boolean;
  success: boolean;
  error?: string;
}

export const defaultAPIRequestState: APIRequestState = {
  pending: false,
  success: false,
  error: null,
};

/** Handling the OPTIONS API Requests
 * (don't blame me pls I didn't write it)
 *
 * In short: The Totus API is funky and OPTIONS on certain endpoints
 * actually returns the fields that can be posted to this endpoint.
 * Inside these fields, some may include "choices"
 * These choices are key - value pairs that can be used to populate
 * Select components (with a bit of fiddling)
 * You can then simply post these values in forms
 * These requests are usually MASSIVE, so be weary when using them
 */
export interface Choice {
  label: string;
  value: string;
}

type APIChoices = Record<string, Choice[]>;

export interface TransferChoices extends APIChoices {
  sender: Choice[];
  request: Choice[];
  destination: Choice[];
  currency: Choice[];
}

const defaultTransferChoices: () => TransferChoices = () => ({
  sender: [],
  request: [],
  destination: [],
  currency: [],
});

export interface TransferRequestChoices extends APIChoices {
  sender: Choice[];
  receiver: Choice[];
  currency: Choice[];
}

const defaultTransferRequestChoices: () => TransferRequestChoices = () => ({
  currency: [],
  receiver: [],
  sender: [],
});

export interface AccountTransferChoices extends APIChoices {
  account: Choice[];
  currency: Choice[];
  status: Choice[];
  wallet: Choice[];
}

const defaultAccountTransferChoices: () => AccountTransferChoices = () => ({
  account: [],
  currency: [],
  status: [],
  wallet: [],
});

export interface AccountChoices extends APIChoices {
  currency: Choice[];
  parent: Choice[];
  receiver: Choice[];
  system: Choice[];
}

const defaultAccountChoices: () => AccountChoices = () => ({
  currency: [],
  parent: [],
  receiver: [],
  system: [],
});

export interface AccountAllocationChoices extends APIChoices {
  account: Choice[];
  provider: Choice[];
  user: Choice[];
}

const defaultAccountAllocationChoices: () => AccountAllocationChoices = () => ({
  account: [],
  provider: [],
  user: [],
});

interface APIChoice {
  display_name: string;
  value: string;
}

interface Options {
  label: string;
  min_value?: number;
  max_value?: number;
  read_only: boolean;
  required: boolean;
  type: 'string';
}

interface OptionsWithChoices extends Options {
  choices: APIChoice[];
}

interface OPTIONSResponse {
  actions: {
    POST: Record<string, Options | OptionsWithChoices>;
  };
  description: string;
  name: string;
  parses: string[];
  renders: string[];
}

interface TransferOPTIONSResponse extends OPTIONSResponse {
  actions: {
    POST: {
      amount: Options;
      currency: OptionsWithChoices;
      date: Options;
      description: Options;
      destination: OptionsWithChoices;
      got_amount: Options;
      got_currency: Options;
      id: Options;
      reference: Options;
      registrar: Options;
      request: OptionsWithChoices;
      sender: OptionsWithChoices;
      status: Options;
      url: Options;
    };
  };
}

interface TransferRequestOPTIONSResponse extends OPTIONSResponse {
  actions: {
    POST: {
      amount: Options;
      currency: OptionsWithChoices;
      date: Options;
      description: Options;
      id: Options;
      receiver: OptionsWithChoices;
      reference: Options;
      requester: Options;
      sender: OptionsWithChoices;
      status: Options;
      url: Options;
    };
  };
}

interface AccountTransferOPTIONSResponse extends OPTIONSResponse {
  actions: {
    POST: {
      account: OptionsWithChoices;
      amount: Options;
      currency: OptionsWithChoices;
      date: Options;
      id: Options;
      requester: Options;
      status: OptionsWithChoices;
      url: Options;
      wallet: OptionsWithChoices;
    };
  };
}

interface AccountOPTIONSResponse extends OPTIONSResponse {
  actions: {
    POST: {
      currency: OptionsWithChoices;
      data: Options;
      disabled: Options;
      id: Options;
      parent: OptionsWithChoices;
      receiver: OptionsWithChoices;
      system: OptionsWithChoices;
      system_name: Options;
      url: Options;
      wallet: OptionsWithChoices;
    };
  };
}

interface AccountAllocationOPTIONSResponse extends OPTIONSResponse {
  actions: {
    POST: {
      account: OptionsWithChoices;
      end_date: Options;
      provider: OptionsWithChoices;
      start_date: Options;
      url: Options;
      user: OptionsWithChoices;
    };
  };
}

/**
 * Takes a set of props and extracts all the choices for each field
 * Useful for populating Select components
 **/
export const retrieveOptions = async <
  A extends APIChoices,
  R extends OPTIONSResponse
>(
  url: string,
  defaultChoicesFactory: () => A
): Promise<A> => {
  const response = await api.options<R>(url);
  const defaultChoices = defaultChoicesFactory();

  for (const prop in defaultChoices) {
    if (response.data.actions.POST?.[prop]) {
      const opts = response.data.actions.POST[prop];

      if (!('choices' in opts)) continue;

      opts.choices.forEach((e) =>
        defaultChoices[prop].push({
          label: e.display_name,
          value: e.value,
        })
      );
    }
  }

  return defaultChoices;
};

/** Handy functions to quickly retrieve OPTIONS for certain endpoints */

export const retrieveTransferChoices = () =>
  retrieveOptions<TransferChoices, TransferOPTIONSResponse>(
    '/transfer/',
    defaultTransferChoices
  );

export const retrieveTransferRequestChoices = () =>
  retrieveOptions<TransferRequestChoices, TransferRequestOPTIONSResponse>(
    '/transfer_request/',
    defaultTransferRequestChoices
  );

export const retrieveAccountTransferChoices = () =>
  retrieveOptions<AccountTransferChoices, AccountTransferOPTIONSResponse>(
    '/account_transfer/',
    defaultAccountTransferChoices
  );

export const retrieveAccountChoices = () =>
  retrieveOptions<AccountChoices, AccountOPTIONSResponse>(
    '/account/',
    defaultAccountChoices
  );

export const retrieveAccountAllocationChoices = () =>
  retrieveOptions<AccountAllocationChoices, AccountAllocationOPTIONSResponse>(
    '/account_allocation/',
    defaultAccountAllocationChoices
  );
